import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

/**
 * Hook for managing events that a company can register lectures for
 */
export const useEventsManager = (companyId, industryId) => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Get company attending events by checking booked booths
   */
  const getCompanyAttendingEvents = useCallback(async (companyIdParam) => {
    const idToUse = companyIdParam || companyId;
    if (!idToUse) return [];
    
    try {
      let companyBookedBooths = [];
      let nextToken = null;
      
      do {
        const response = await client.graphql({
          query: /* GraphQL */ `
            query ListBookedBooths(
              $filter: ModelBookedBoothFilterInput
              $limit: Int
              $nextToken: String
            ) {
              listBookedBooths(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                  id
                  event {
                    id
                  }
                }
                nextToken
              }
            }
          `,
          variables: {
            filter: {
              companyBookedBoothsId: {
                eq: idToUse
              }
            },
            limit: 100,
            nextToken: nextToken
          }
        });

        companyBookedBooths = companyBookedBooths.concat(response.data.listBookedBooths.items);
        nextToken = response.data.listBookedBooths.nextToken;
      } while (nextToken);
      
      return companyBookedBooths.map(item => item.event.id);
    } catch (error) {
      console.log("Error fetching company attending events:", error);
      return [];
    }
  }, [companyId]);

  /**
   * Fetch events where company can register lectures
   */
  const fetchEvents = useCallback(async () => {
    if (!companyId || !industryId) return [];

    setIsLoading(true);
    setError(null);
    
    try {
      // Get events by getting industry events
      const companyAttendingEventIds = await getCompanyAttendingEvents();
      
      if (companyAttendingEventIds.length === 0) {
        setEvents([]);
        return [];
      }

      const eventsResponse = await client.graphql({
        query: /* GraphQL */ `
          query ListIndustryEvents(
            $filter: ModelIndustryEventsFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listIndustryEvents(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                event {
                  id
                  startDate
                  endDate
                  lectureRegistrationStartDate
                  lectureRegistrationEndDate
                  lectureEditEndDate
                  lecturePresentationEndDate
                  lectureDocumentsEndDate
                  exhibitorLectures
                  status
                  name {
                    language
                    text
                  }
                  description {
                    language
                    text
                  }
                  location {
                    name {
                      language
                      text
                    }
                    address {
                      street
                      city
                      countryCode
                    }
                  }
                }
              }
              nextToken
            }
          }
        `,
        variables: {
          filter: {
            and: {
              industryId: {
                eq: industryId
              },
              or: companyAttendingEventIds.map(id => ({
                eventId: {
                  eq: id
                }
              }))
            }
          }
        }
      });

      // Filter events that are active, allow exhibitor lectures, and are still relevant
      const filteredEvents = eventsResponse.data.listIndustryEvents.items
        .filter(item => {
          const event = item.event;
          return (
            event.status === "active" && 
            event.exhibitorLectures && 
            event.lectureRegistrationStartDate && 
            event.endDate >= new Date().toISOString().substring(0, 10)
          );
        })
        .map(item => item.event)
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)); // Sort by start date

      setEvents(filteredEvents);
      console.log("Fetched events:", filteredEvents);
      
      return filteredEvents;
    } catch (error) {
      console.log("Error fetching events:", error);
      setError(error.message || 'Failed to fetch events');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [companyId, industryId, getCompanyAttendingEvents]);

  /**
   * Check if lecture registration is open for an event
   */
  const isRegistrationOpen = useCallback((event) => {
    if (!event.lectureRegistrationStartDate || !event.lectureRegistrationEndDate) {
      return false;
    }
    
    const now = new Date();
    
    // Parse start date and set to beginning of day (00:00:00) in local timezone
    const startDate = new Date(event.lectureRegistrationStartDate);
    startDate.setHours(0, 0, 0, 0);
    
    // Parse end date and set to end of day (23:59:59) in local timezone  
    const endDate = new Date(event.lectureRegistrationEndDate);
    endDate.setHours(23, 59, 59, 999);
    
    return now >= startDate && now <= endDate;
  }, []);

  /**
   * Check if lecture registration will open in the future
   */
  const isRegistrationUpcoming = useCallback((event) => {
    if (!event.lectureRegistrationStartDate) {
      return false;
    }
    
    const now = new Date();
    const startDate = new Date(event.lectureRegistrationStartDate);
    
    return now < startDate;
  }, []);

  /**
   * Check if lecture registration has ended
   */
  const isRegistrationClosed = useCallback((event) => {
    if (!event.lectureRegistrationEndDate) {
      return false;
    }
    
    const now = new Date();
    const endDate = new Date(event.lectureRegistrationEndDate);
    
    return now > endDate;
  }, []);

  /**
   * Get events by registration status
   */
  const getEventsByRegistrationStatus = useCallback((status = 'all') => {
    switch (status) {
      case 'open':
        return events.filter(event => isRegistrationOpen(event));
      case 'upcoming':
        return events.filter(event => isRegistrationUpcoming(event));
      case 'closed':
        return events.filter(event => isRegistrationClosed(event));
      case 'active':
        return events.filter(event => 
          isRegistrationOpen(event) || isRegistrationUpcoming(event)
        );
      default:
        return events;
    }
  }, [events, isRegistrationOpen, isRegistrationUpcoming, isRegistrationClosed]);

  /**
   * Get event by ID
   */
  const getEventById = useCallback((eventId) => {
    return events.find(event => event.id === eventId);
  }, [events]);

  /**
   * Check if documents can be uploaded for an event
   */
  const canUploadDocuments = useCallback((event) => {
    if (!event.lectureDocumentsEndDate) return false;
    return new Date(event.lectureDocumentsEndDate) >= new Date();
  }, []);

  /**
   * Check if presentations can be uploaded for an event
   */
  const canUploadPresentations = useCallback((event) => {
    if (!event.lecturePresentationEndDate) return false;
    return new Date(event.lecturePresentationEndDate).setHours(23, 59, 59, 999) >= new Date();
  }, []);

  /**
   * Check if lectures can be edited for an event
   */
  const canEditLectures = useCallback((event) => {
    // Can edit if event hasn't started and edit end date hasn't passed
    const now = new Date();
    const eventStarted = event.startDate && new Date(event.startDate) <= now;
    const editDeadlinePassed = event.lectureEditEndDate && new Date(event.lectureEditEndDate) < now;
    
    return !eventStarted && !editDeadlinePassed;
  }, []);

  // Auto-fetch when companyId or industryId changes
  useEffect(() => {
    if (companyId && industryId) {
      fetchEvents();
    }
  }, [companyId, industryId, fetchEvents]);

  return {
    // Data
    events,
    
    // Loading states
    isLoading,
    error,
    
    // Actions
    fetchEvents,
    refreshEvents: fetchEvents,
    
    // Event status checks
    isRegistrationOpen,
    isRegistrationUpcoming,
    isRegistrationClosed,
    
    // Permission checks
    canUploadDocuments,
    canUploadPresentations,
    canEditLectures,
    
    // Filtering and utility
    getEventsByRegistrationStatus,
    getEventById
  };
};